import type { ReactNode } from 'react';
import { DateTime } from 'luxon';
import { Box } from '../../../components/UI-CHAKRA/Box';
import { Flex } from '../../../components/UI-CHAKRA/Flex';
import { Text } from '../../../components/UI-CHAKRA/Text';
import { TIMEZONE } from '../../../helpers/Date';
import { usePrescriptionStore } from '../../../stores/prescriptionStore';
import {
  SNCR_ALERT_RED_MESSAGES,
  getSncrAlertDisplay,
  getTransitionWindowEnd,
  isSncrFallbackFlowEnabled,
} from './sncrAlert';

const ALERT_STYLES = {
  amarelo: { bg: '#FFF4C0', border: '#B19100', icon: '#7A5405', text: '#1E252B' },
  vermelho: { bg: '#FFDBDB', border: '#FF6161', icon: '#8B0808', text: '#8B0808' },
} as const;

function WarningTriangleIcon({ color }: { color: string }) {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M15.4 13.33 10.96 5.55a1.1 1.1 0 0 0-1.92 0L4.6 13.33a1.1 1.1 0 0 0 .96 1.65h8.88a1.1 1.1 0 0 0 .96-1.65Z"
        stroke={color}
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M10 8.33v2.22" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10 12.78h.006" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * Alerta informativo de numeração SNCR (Task 4, Epic MEVO-5349) — renderizado
 * dentro do card de "Gerenciar assinatura digital" (`DialogCertificates.tsx`).
 *
 * Só decide cor/texto a partir do resultado de `getSncrAlertDisplay` (Task 2) —
 * nunca lê status de certificado/certificadora (Camada 1, à parte, não tocada
 * aqui). Atrás da feature flag de rollout da Task 8: com ela desligada, este
 * componente nunca renderiza nada (rollback integral).
 */
export function SncrFallbackAlert() {
  const prescription = usePrescriptionStore((store) => store.prescription);
  const prescriberConfiguration = usePrescriptionStore((store) => store.prescriberConfiguration);
  const selectedCertificateType = usePrescriptionStore((store) => store.selectedCertificateType);

  if (!isSncrFallbackFlowEnabled(prescriberConfiguration)) return null;

  const { variant, reason } = getSncrAlertDisplay(
    prescription?.Itens,
    prescriberConfiguration,
    new Date(),
    selectedCertificateType
  );

  if (variant === 'nenhum') return null;

  const styles = ALERT_STYLES[variant];

  let content: ReactNode;

  if (variant === 'amarelo') {
    const transitionWindowEndLabel = DateTime.fromJSDate(getTransitionWindowEnd(prescriberConfiguration), {
      zone: TIMEZONE,
    }).toFormat('dd/MM/yyyy');

    content = (
      <Text fontSize="12px" lineHeight="normal" color={ALERT_STYLES.amarelo.text}>
        <Box as="span" fontWeight={500}>
          Conforme determinação da Anvisa anunciada dia 29/10/2026, as Receitas de Controle Especial e as Receitas
          Sujeitas à Retenção{' '}
        </Box>
        <Box as="span" fontWeight={600}>
          emitidas sem numeração do SNCR poderão ser emitidas até dia {transitionWindowEndLabel}
        </Box>
        <Box as="span" fontWeight={500}>
          , podendo ser aceitas de acordo com sua validade em regulação.
        </Box>
      </Text>
    );
  } else {
    content = SNCR_ALERT_RED_MESSAGES[reason ?? 'brancaSemNumeracaoForaDaJanela'];
  }

  return (
    <Flex
      bg={styles.bg}
      border={`1px solid ${styles.border}`}
      borderRadius="8px"
      padding="8px"
      gap="4px"
      alignItems="center"
    >
      <Box flexShrink={0}>
        <WarningTriangleIcon color={styles.icon} />
      </Box>
      {variant === 'vermelho' ? (
        <Text fontSize="12px" fontWeight={600} lineHeight="120%" color={styles.text} whiteSpace="pre-line">
          {content}
        </Text>
      ) : (
        content
      )}
    </Flex>
  );
}
